<script setup lang="ts">
import { ref, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { ArrowLeft, Phone, Mail, CheckCircle, XCircle, ShieldAlert, RotateCcw } from 'lucide-vue-next';
import { clientsAPI, type Client } from '../services/api';
import { serverMessage } from '../composables/useVisits';

const route = useRoute();
const router = useRouter();
const { t } = useI18n();

const client = ref<Client | null>(null);
const isLoading = ref(false);
const error = ref<string | null>(null);

const isResetting = ref(false);
const resetError = ref('');

const resetOffensive = async (): Promise<void> => {
  if (!client.value) return;

  isResetting.value = true;
  resetError.value = '';

  try {
    client.value = (await clientsAPI.resetOffensive(client.value.id)).data;
  } catch (err) {
    resetError.value = serverMessage(err) || t('clients.offensive_reset_error');
  } finally {
    isResetting.value = false;
  }
};

const formatDate = (dateStr: string): string => {
  return new Date(dateStr).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

onMounted(async () => {
  const id = route.params.id as string;
  isLoading.value = true;
  error.value = null;

  try {
    client.value = (await clientsAPI.getById(id)).data;
  } catch (err: any) {
    error.value = err.response?.data?.message || t('composables.clients.error_fetch_one');
  } finally {
    isLoading.value = false;
  }
});
</script>

<template>
  <div class="flex flex-col min-h-full bg-[#0A0A0B]">
    <div class="flex-1 p-4 md:p-6 lg:p-8">
      <div class="max-w-3xl mx-auto">
        <!-- Back link -->
        <button
          @click="router.push('/clients')"
          class="flex items-center gap-2 text-sm text-[#524840] hover:text-[#F5F0EA] mb-6 transition"
        >
          <ArrowLeft class="w-4 h-4" />
          {{ t('clients.back_to_list') }}
        </button>

        <!-- Loading state -->
        <div
          v-if="isLoading"
          class="bg-[#111113] rounded-xl border border-[#2A2018] p-8 text-center text-[#524840]"
        >
          {{ t('clients.loading') }}
        </div>

        <!-- Error state -->
        <div
          v-else-if="error"
          class="p-4 bg-red-900/20 border border-red-800 rounded-xl text-red-400"
        >
          {{ error }}
        </div>

        <!-- Client card -->
        <div v-else-if="client" class="space-y-4">
          <!-- Header card -->
          <div class="bg-[#111113] rounded-xl border border-[#2A2018] p-6">
            <div class="flex items-center gap-4">
              <div
                class="w-16 h-16 bg-teal-900/30 rounded-full flex items-center justify-center flex-shrink-0"
              >
                <span class="text-teal-300 text-2xl font-semibold">
                  {{ client.name[0]?.toUpperCase() }}
                </span>
              </div>
              <div>
                <h2 class="text-xl font-bold text-[#F5F0EA]">
                  {{ client.name }}
                </h2>
                <div class="flex items-center gap-2 mt-1">
                  <span
                    :class="[
                      'inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium',
                      client.confirmed
                        ? 'bg-green-900/30 text-green-400'
                        : 'bg-[#1E1E23] text-[#524840]',
                    ]"
                  >
                    <CheckCircle v-if="client.confirmed" class="w-3.5 h-3.5" />
                    <XCircle v-else class="w-3.5 h-3.5" />
                    {{ client.confirmed ? t('clients.confirmed_yes') : t('clients.confirmed_no') }}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <!-- Contact info card -->
          <div class="bg-[#111113] rounded-xl border border-[#2A2018] p-6">
            <h3 class="text-sm font-semibold text-[#524840] uppercase tracking-wider mb-4">
              {{ t('clients.contact_info') }}
            </h3>
            <div class="space-y-3">
              <div class="flex items-center gap-3">
                <Phone class="w-4 h-4 text-[#524840] flex-shrink-0" />
                <span class="text-sm text-[#9E9080]">
                  {{ client.data.phone_number || t('clients.no_phone') }}
                </span>
              </div>
              <div class="flex items-center gap-3">
                <Mail class="w-4 h-4 text-[#524840] flex-shrink-0" />
                <span class="text-sm text-[#9E9080]">
                  {{ client.data.email || t('clients.no_email') }}
                </span>
              </div>
            </div>
          </div>

          <!-- Offensive card -->
          <div class="bg-[#111113] rounded-xl border border-[#2A2018] p-6">
            <h3 class="text-sm font-semibold text-[#524840] uppercase tracking-wider mb-4">
              {{ t('clients.offensive_section') }}
            </h3>
            <div class="flex flex-wrap items-center justify-between gap-4">
              <div class="flex items-center gap-3">
                <ShieldAlert
                  class="w-4 h-4 flex-shrink-0"
                  :class="client.offensive > 0 ? 'text-red-400' : 'text-[#524840]'"
                />
                <div>
                  <p class="text-sm text-[#F5F0EA]">
                    {{ t('clients.offensive_count', { count: client.offensive }) }}
                  </p>
                  <p class="text-xs text-[#524840] mt-0.5">{{ t('clients.offensive_hint') }}</p>
                </div>
              </div>
              <button
                type="button"
                :disabled="isResetting || client.offensive === 0"
                @click="resetOffensive"
                class="inline-flex items-center gap-2 px-4 py-2 bg-[#1E1E23] border border-[#2A2018] hover:border-[#D4956A] text-sm text-[#F5F0EA] rounded-2xl transition disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:border-[#2A2018]"
              >
                <RotateCcw class="w-4 h-4" />
                {{ isResetting ? t('clients.offensive_resetting') : t('clients.offensive_reset') }}
              </button>
            </div>
            <p v-if="resetError" class="mt-3 text-xs text-red-400">{{ resetError }}</p>
          </div>

          <!-- Metadata card -->
          <div class="bg-[#111113] rounded-xl border border-[#2A2018] p-6">
            <dl class="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <dt class="text-xs font-medium text-[#524840] uppercase tracking-wider">
                  {{ t('clients.source') }}
                </dt>
                <dd class="mt-1">
                  <span
                    v-if="client.source"
                    class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-900/30 text-blue-400"
                  >
                    {{ client.source.name }}
                  </span>
                  <span v-else class="text-sm text-[#9E9080]">—</span>
                </dd>
              </div>
              <div>
                <dt class="text-xs font-medium text-[#524840] uppercase tracking-wider">
                  {{ t('clients.created_at') }}
                </dt>
                <dd class="mt-1 text-sm text-[#F5F0EA]">
                  {{ formatDate(client.created_at) }}
                </dd>
              </div>
              <div>
                <dt class="text-xs font-medium text-[#524840] uppercase tracking-wider">ID</dt>
                <dd class="mt-1 text-sm text-[#524840] font-mono break-all">
                  {{ client.id }}
                </dd>
              </div>
            </dl>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
